import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtKg, fmtEur, fmtNum, fmtDate, fmtMonat, aktuellerMonat } from "@/lib/api";
import { KpiCard } from "@/components/app/KpiCard";
import { QualityBadge } from "@/components/app/QualityBadge";
import { MonthPicker } from "@/components/app/MonthPicker";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Milk, Banknote, FlaskConical, ShieldCheck, Users, Upload, Loader2, Tractor,
  CheckCircle2, AlertTriangle, ArrowRight, Ban, ShieldAlert, ListOrdered, TrendingUp,
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { PortalHinweisBanner } from "@/components/portal/PortalHinweisBanner";

const LieferantDashboard = () => {
  const { liefernummer } = useAuth();
  const [monat, setMonat] = useState(aktuellerMonat());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!liefernummer) return;
    setLoading(true);
    api
      .get("/dashboard/lieferant", { params: { liefernummer, monat } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [liefernummer, monat]);

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const ab = data?.abrechnung;
  const letzteProbe = data?.letzte_proben?.[0];
  const hemmstoffOk = !data?.letzte_proben?.some((p) => p.hemmstoff);
  const GRUND_LABELS = {
    hemmstoff: "Hemmstoff-Befund", keimzahl: "Keimzahl-Grenzwertüberschreitung",
    zellzahl: "Zellzahl-Grenzwertüberschreitung", manuell: "Manuelle Sperre",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Liefernummer {liefernummer} · {fmtMonat(monat)}</p>
        </div>
        <MonthPicker value={monat} onChange={setMonat} />
      </div>

      {data?.aktive_sperren?.length > 0 && (
        <div data-testid="supplier-block-banner" className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <Ban className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
            <div className="space-y-1.5">
              <p className="font-semibold text-destructive">Liefersperre aktiv</p>
              {data.aktive_sperren.map((s) => (
                <p key={s.id} className="text-sm">
                  <span className="font-medium">{GRUND_LABELS[s.grund] || s.grund}:</span>{" "}
                  <span className="text-muted-foreground">{s.details}</span>
                </p>
              ))}
              <p className="text-xs text-muted-foreground">
                Bitte kontaktieren Sie umgehend die Molkerei Seifried GmbH zur Klärung und Freigabe.
              </p>
            </div>
          </div>
        </div>
      )}

      <PortalHinweisBanner />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testId="kpi-liefermenge" icon={Milk} label="Liefermenge" value={fmtKg(data?.menge_kg)} sub={`${data?.anzahl_lieferungen || 0} Lieferungen`} />
        <KpiCard testId="kpi-milchgeld" icon={Banknote} label="Milchgeld"
          value={ab ? fmtEur(ab.brutto_eur) : "–"}
          sub={ab ? `${fmtMonat(ab.monat)} · ${fmtNum(ab.cent_je_kg, 2)} ct/kg` : "Noch keine Abrechnung"} />
        <KpiCard testId="kpi-qualitaet" icon={ShieldCheck} label="Qualitätsklasse"
          value={ab ? <QualityBadge klasse={ab.qualitaetsklasse} icon={ab.qualitaetsklasse_icon} farbe={ab.qualitaetsklasse_farbe} showText /> : "–"}
          sub={ab ? `Zellzahl Ø ${fmtNum(ab.zellzahl_geo)} · Keimzahl Ø ${fmtNum(ab.keimzahl_geo)}` : ""} />
        <KpiCard testId="kpi-letzte-probe" icon={FlaskConical} label="Letzte Probe"
          value={letzteProbe ? fmtDate(letzteProbe.datum) : "–"}
          sub={letzteProbe ? `Fett ${fmtNum(letzteProbe.fett, 2)} % · Eiweiß ${fmtNum(letzteProbe.eiweiss, 2)} %` : ""} />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Anlieferung im Monatsverlauf</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          {data?.tagesserie?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.tagesserie} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(213 20% 86%)" />
                <XAxis dataKey="datum" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} width={50} />
                <Tooltip formatter={(v) => [`${fmtNum(v)} kg`, "Menge"]} labelFormatter={fmtDate} />
                <Area type="monotone" dataKey="menge_kg" stroke="hsl(217 72% 42%)" fill="rgba(30,102,204,0.16)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={Milk} title="Keine Lieferungen" text="Für den gewählten Monat liegen keine Lieferungen vor." />
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Letzte Milchproben</CardTitle>
            <Button asChild variant="ghost" size="sm" data-testid="dashboard-zu-proben-link">
              <Link to="/milchproben">Alle Proben <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </CardHeader>
          <CardContent>
            {data?.letzte_proben?.length ? (
              <div className="overflow-x-auto">
                <Table data-testid="dashboard-proben-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Fett %</TableHead>
                      <TableHead className="text-right">Eiweiß %</TableHead>
                      <TableHead className="text-right">Zellzahl</TableHead>
                      <TableHead className="text-right">Keimzahl</TableHead>
                      <TableHead>Hemmstoff</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.letzte_proben.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{fmtDate(p.datum)}</TableCell>
                        <TableCell className="text-right font-num">{fmtNum(p.fett, 2)}</TableCell>
                        <TableCell className="text-right font-num">{fmtNum(p.eiweiss, 2)}</TableCell>
                        <TableCell className="text-right font-num">{fmtNum(p.zellzahl)}</TableCell>
                        <TableCell className="text-right font-num">{fmtNum(p.keimzahl)}</TableCell>
                        <TableCell>
                          {p.hemmstoff ? (
                            <Badge variant="destructive">positiv</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">unauffällig</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <EmptyState icon={FlaskConical} title="Keine Proben vorhanden" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border p-3">
              {hemmstoffOk ? (
                <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
              )}
              <div>
                <p className="text-sm font-medium">Hemmstoff</p>
                <p className="text-xs text-muted-foreground">
                  {hemmstoffOk ? "Alle aktuellen Proben unauffällig." : "Achtung: positiver Hemmstoff-Befund in einer aktuellen Probe!"}
                </p>
              </div>
            </div>
            {ab && (
              <div className="flex items-start gap-3 rounded-lg border p-3">
                <Banknote className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Letzte Abrechnung</p>
                  <p className="text-xs text-muted-foreground">
                    {fmtMonat(ab.monat)}: {fmtEur(ab.brutto_eur)} brutto bei {fmtKg(ab.menge_kg)}.
                  </p>
                  <Button asChild variant="link" size="sm" className="px-0 h-7" data-testid="dashboard-zu-abrechnung-link">
                    <Link to="/abrechnungen">Zur Abrechnung <ArrowRight className="h-3 w-3 ml-1" /></Link>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const RANKING_KRITERIEN = [
  { key: "menge", label: "Liefermenge" },
  { key: "qualitaet", label: "Qualität" },
  { key: "zellzahl", label: "Zellzahl" },
  { key: "keimzahl", label: "Keimzahl" },
  { key: "fett", label: "Fett %" },
  { key: "eiweiss", label: "Eiweiß %" },
  { key: "milchgeld", label: "Auszahlung ct/kg" },
];

const rankWert = (r, kriterium) => {
  switch (kriterium) {
    case "zellzahl": return fmtNum(r.zellzahl_geo);
    case "keimzahl": return fmtNum(r.keimzahl_geo);
    case "fett": return `${fmtNum(r.fett_prozent, 2)} %`;
    case "eiweiss": return `${fmtNum(r.eiweiss_prozent, 2)} %`;
    case "milchgeld": return `${fmtNum(r.cent_je_kg, 2)} ct/kg`;
    case "qualitaet": return fmtNum(r.zellzahl_geo);
    default: return fmtKg(r.menge_kg);
  }
};

const GRUND_KURZ = {
  hemmstoff: "Hemmstoff", keimzahl: "Keimzahl", zellzahl: "Zellzahl", manuell: "Manuell",
};

const StaffDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState(null);
  const [kriterium, setKriterium] = useState("menge");
  const [langzeit, setLangzeit] = useState(null);

  useEffect(() => {
    api.get("/dashboard/admin").then((res) => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/statistik/langzeit", { params: { monate: 12 } }).then((res) => setLangzeit(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/statistik/ranking", { params: { kriterium } }).then((res) => setRanking(res.data)).catch(() => {});
  }, [kriterium]);

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const istAdmin = user.rolle === "admin" || user.rolle === "sysadmin";
  const probleme = (data?.anzahl_aktive_sperren || 0) + (data?.anzahl_verstoesse_kritisch || 0) > 0;
  const langzeitSerie = (langzeit?.serie || []).map((s) => ({ ...s, monat_kurz: s.monat?.slice(2).replace("-", "/") }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          {istAdmin ? "Molkerei-Übersicht" : "HofBerater-Übersicht"} · {fmtMonat(data?.monat)}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testId="kpi-lieferanten" icon={Tractor} label="Aktive Lieferanten" value={fmtNum(data?.anzahl_lieferanten)} />
        <KpiCard testId="kpi-monatsmenge" icon={Milk} label="Monatsmenge" value={fmtKg(data?.monatsmenge_kg)} sub={`${fmtNum(data?.anzahl_lieferungen_monat)} Lieferungen`} />
        <KpiCard testId="kpi-sperren" icon={Ban} label="Aktive Sperren" value={fmtNum(data?.anzahl_aktive_sperren)}
          sub={data?.anzahl_verstoesse_kritisch ? `${fmtNum(data.anzahl_verstoesse_kritisch)} kritische Verstöße` : "Keine kritischen Verstöße"} />
        <KpiCard testId="kpi-benutzer" icon={Users} label="Benutzer" value={fmtNum(data?.anzahl_benutzer)} />
      </div>

      {probleme && (
        <div data-testid="admin-problem-banner" className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-destructive">Probleme erfordern Ihre Aufmerksamkeit</p>
                <p className="text-sm text-muted-foreground">
                  {fmtNum(data?.anzahl_aktive_sperren)} aktive Liefersperre(n) · {fmtNum(data?.anzahl_verstoesse_kritisch)} kritische
                  Verstöße · {fmtNum(data?.anzahl_verstoesse_warnung)} Warnung(en) in den letzten 3 Monaten
                </p>
              </div>
            </div>
            {istAdmin && (
              <Button asChild variant="destructive" size="sm" data-testid="problem-banner-link">
                <Link to="/admin/qualitaet?tab=sperren">Qualität &amp; Sperren <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Aktive Sperren</CardTitle>
            {istAdmin && (
              <Button asChild variant="ghost" size="sm" data-testid="dashboard-sperren-link">
                <Link to="/admin/qualitaet?tab=sperren">Verwalten <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {data?.aktive_sperren?.length ? (
              <div className="space-y-2" data-testid="dashboard-sperren-liste">
                {data.aktive_sperren.slice(0, 5).map((s) => (
                  <Link
                    key={s.id}
                    to={istAdmin
                      ? `/admin/qualitaet?tab=sperren&liefernummer=${s.liefernummer}`
                      : `/lieferanten?liefernummer=${s.liefernummer}`}
                    data-testid={`dashboard-sperre-item-${s.liefernummer}`}
                    className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.liefernummer} · {s.lieferant_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.details}</p>
                    </div>
                    <Badge variant="destructive" className="shrink-0 ml-2">{GRUND_KURZ[s.grund] || s.grund}</Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> Keine aktiven Sperren.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Aktuelle Verstöße &amp; Warnungen</CardTitle>
            {istAdmin && (
              <Button asChild variant="ghost" size="sm" data-testid="dashboard-verstoesse-link">
                <Link to="/admin/qualitaet?tab=verstoesse">Verwalten <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {data?.verstoesse?.length ? (
              <div className="space-y-2" data-testid="dashboard-verstoesse-liste">
                {data.verstoesse.slice(0, 5).map((v) => (
                  <Link
                    key={v.id}
                    to={istAdmin
                      ? `/admin/qualitaet?tab=verstoesse&liefernummer=${v.liefernummer}`
                      : `/lieferanten?liefernummer=${v.liefernummer}`}
                    data-testid={`dashboard-verstoss-item-${v.liefernummer}`}
                    className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="min-w-0">
                      <p className="font-medium truncate">{v.liefernummer} · {v.lieferant_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{v.details}</p>
                    </div>
                    <Badge
                      variant={v.schwere === "kritisch" ? "destructive" : "outline"}
                      className={`shrink-0 ml-2 ${v.schwere === "warnung" ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]" : ""}`}
                    >
                      {v.schwere}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> Keine Verstöße in den letzten Monaten.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" /> Gesamtanliefermenge (12 Monate)
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {langzeitSerie.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={langzeitSerie} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(213 20% 86%)" />
                  <XAxis dataKey="monat_kurz" tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} width={60}
                    tickFormatter={(v) => `${Math.round(v / 1000)}t`} />
                  <Tooltip formatter={(v) => [`${fmtNum(v)} kg`, "Gesamtmenge"]} labelFormatter={(l) => `Monat ${l}`} />
                  <Area type="monotone" dataKey="menge_kg" stroke="hsl(217 72% 42%)" fill="rgba(30,102,204,0.16)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex justify-center items-center h-full"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-primary" /> Ø Laborwerte aller Lieferanten (12 Monate)
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {langzeitSerie.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={langzeitSerie} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(213 20% 86%)" />
                  <XAxis dataKey="monat_kurz" tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} />
                  <YAxis yAxisId="zz" tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} width={55}
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <YAxis yAxisId="kz" orientation="right" tick={{ fontSize: 11, fill: "hsl(215 16% 40%)" }} width={50}
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v, name) => [fmtNum(v), name]} labelFormatter={(l) => `Monat ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="zz" type="monotone" dataKey="zellzahl" name="Ø Zellzahl" stroke="hsl(217 72% 42%)" strokeWidth={2} dot={false} />
                  <Line yAxisId="kz" type="monotone" dataKey="keimzahl" name="Ø Keimzahl" stroke="hsl(190 80% 40%)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex justify-center items-center h-full"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ListOrdered className="h-4 w-4 text-primary" /> Lieferanten-Ranking · {fmtMonat(ranking?.monat)}
          </CardTitle>
          <Select value={kriterium} onValueChange={setKriterium}>
            <SelectTrigger data-testid="ranking-criterion-select" className="w-[190px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANKING_KRITERIEN.map((k) => (
                <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          {ranking?.eintraege?.length ? (
            <div className="overflow-x-auto">
              <Table data-testid="ranking-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Rang</TableHead>
                    <TableHead>Liefernr.</TableHead>
                    <TableHead>Lieferant</TableHead>
                    <TableHead>Klasse</TableHead>
                    <TableHead className="text-right">Menge</TableHead>
                    {kriterium !== "menge" && (
                      <TableHead className="text-right">{RANKING_KRITERIEN.find((k) => k.key === kriterium)?.label}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ranking.eintraege.slice(0, 10).map((r) => (
                    <TableRow key={r.id || r.liefernummer}>
                      <TableCell className="font-num font-semibold">{r.rang}</TableCell>
                      <TableCell className="font-num">{r.liefernummer}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{r.lieferant_name}</TableCell>
                      <TableCell><QualityBadge klasse={r.qualitaetsklasse} icon={r.qualitaetsklasse_icon} farbe={r.qualitaetsklasse_farbe} /></TableCell>
                      <TableCell className="text-right font-num">{fmtNum(r.menge_kg)}</TableCell>
                      {kriterium !== "menge" && (
                        <TableCell className="text-right font-num font-medium">{rankWert(r, kriterium)}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {ranking.durchschnitt && (
                <p className="px-4 py-3 text-xs text-muted-foreground border-t">
                  Ø aller Lieferanten: {fmtKg(ranking.durchschnitt.menge_kg)} · Fett {fmtNum(ranking.durchschnitt.fett_prozent, 2)} % ·
                  Eiweiß {fmtNum(ranking.durchschnitt.eiweiss_prozent, 2)} % · Zellzahl {fmtNum(ranking.durchschnitt.zellzahl_geo)} ·
                  Keimzahl {fmtNum(ranking.durchschnitt.keimzahl_geo)} · {fmtNum(ranking.durchschnitt.cent_je_kg, 2)} ct/kg
                </p>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-10 text-sm text-muted-foreground">Noch keine Abrechnungsdaten für das Ranking.</div>
          )}
        </CardContent>
      </Card>

      {user.rolle === "berater" && (
        <Card>
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">Lieferanten einsehen</p>
              <p className="text-sm text-muted-foreground">Filtern Sie nach Gruppen oder Milchsorten und öffnen Sie Detaildaten.</p>
            </div>
            <Button asChild data-testid="berater-zu-lieferanten-button">
              <Link to="/lieferanten">Zur Lieferantenübersicht <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {user.rolle === "admin" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">Letzte Importe</CardTitle>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/import">ÖNORM-Import <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </CardHeader>
            <CardContent>
              {data?.letzte_importe?.length ? (
                <div className="space-y-2">
                  {data.letzte_importe.map((i) => (
                    <div key={i.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{i.dateiname}</p>
                        <p className="text-xs text-muted-foreground">{i.neu_importiert} neu · {fmtKg(i.summe_kg)}</p>
                      </div>
                      <Badge variant={i.status === "erfolgreich" ? "secondary" : "destructive"}>{i.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Upload} title="Noch keine Importe" text="Laden Sie einen ÖNORM-Datenträger hoch oder starten Sie die FTP-Abholung." />
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">Letzte Reihungsdateien</CardTitle>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/reihungsdateien">Verwalten <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </CardHeader>
            <CardContent>
              {data?.letzte_reihungen?.length ? (
                <div className="space-y-2">
                  {data.letzte_reihungen.map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{r.dateiname}</p>
                        <p className="text-xs text-muted-foreground">{r.anzahl} Proben · Tour {r.tour}</p>
                      </div>
                      <Badge variant={r.status === "uebertragen" ? "secondary" : "outline"}>
                        {r.status === "uebertragen" ? "übertragen" : "erstellt"}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={FlaskConical} title="Noch keine Reihungsdateien" text="Generieren Sie Reihungsdateien aus importierten Probenahmen." />
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  return user?.rolle === "lieferant" ? <LieferantDashboard /> : <StaffDashboard />;
}
